import type {
	ArticleTranslationFailedData,
	FileProcessingFailedData,
	MessageTranslationFailedData,
	RealtimeMessage,
	ValidationAssetFailedData,
} from '@atlas/types'
import { EventType } from '@atlas/types'
import { notification } from '@/notifications/notification-store'
import { subscribeRealtime } from './realtime-dispatcher'

// Turns the realtime failures a user must be told about into notifications
// (ADR-0020). Separate from the query bridge because the two answer different
// questions: that one keeps data fresh, this one speaks to the user.
//
// Registered once for the session, so a background failure is reported wherever
// the user happens to be — the page that started the work is usually not the page
// they are on when it finishes.
//
// Only failures are announced. A successful thumbnail is already visible the
// moment it renders, and a success notification per file would put forty of them
// on screen for one library page; the surface exists to make the invisible
// visible, not to narrate what the user can see.
//
// A failure belongs here when the hook waiting on it invalidates without reading
// the payload — file processing, validation-asset renders and translations all
// do, so their errors would otherwise be silent for a user who navigated away.

/** Payload of any failure this bridge announces. Each type names its reason differently. */
type FailureData = Partial<
	FileProcessingFailedData & ValidationAssetFailedData & MessageTranslationFailedData & ArticleTranslationFailedData
>

/**
 * Reads the first present string among `keys` off the payload.
 *
 * The payload is treated as untrusted shape rather than cast: a message is a
 * hint, and a drifted or partial `data` must degrade to generic copy instead of
 * rendering `undefined` at the user.
 *
 * @param {RealtimeMessage} message - The failure message received from the channel.
 * @param {Array<keyof FailureData>} keys - Candidate fields, in preference order.
 * @returns {string | null} The first string value found, or null.
 */
function stringField(message: RealtimeMessage, keys: Array<keyof FailureData>): string | null {
	const data = message.data as FailureData | undefined
	for (const key of keys) {
		const value = data?.[key]
		if (typeof value === 'string' && value.length > 0) return value
	}
	return null
}

/**
 * Announces one failure, prefixed with what failed when the payload names it.
 *
 * Each failure gets its own generic fallback: "Processing failed." on a
 * translation would misdescribe the work the user is waiting on.
 *
 * @param {RealtimeMessage} message - The failure message received from the channel.
 * @param {Array<keyof FailureData>} reasonKeys - Where this event carries its reason.
 * @param {string} fallback - Copy to use when the payload carries no reason.
 */
function announceFailure(message: RealtimeMessage, reasonKeys: Array<keyof FailureData>, fallback: string): void {
	const subject = stringField(message, ['fileName'])
	const reason = stringField(message, reasonKeys) ?? fallback
	notification.error(subject ? `${subject}: ${reason}` : reason, {
		entityRef: { type: message.entityRef.type, id: message.entityRef.id },
	})
}

/**
 * Subscribes the notification surface to the channel. Called once by
 * `RealtimeProvider`.
 *
 * Covers every failure whose originating hook deliberately ignores the payload
 * and only invalidates — for those, this bridge is the sole path to the user, and
 * it must stay registered for the session so a failure still lands after the user
 * has navigated away from the page that started the work.
 *
 * @returns {() => void} Unsubscribe function, for the provider's cleanup.
 */
export function registerNotificationBridge(): () => void {
	const unsubscribes = [
		subscribeRealtime({ type: EventType.FILE_PROCESSING_FAILED }, (message) => {
			announceFailure(message, ['errorMessage'], 'Processing failed.')
		}),
		subscribeRealtime({ type: EventType.VALIDATION_ASSET_FAILED }, (message) => {
			announceFailure(message, ['error'], 'Validation asset generation failed.')
		}),
		subscribeRealtime({ type: EventType.MESSAGE_TRANSLATION_FAILED }, (message) => {
			announceFailure(message, ['reason'], 'Translation failed.')
		}),
		// Its own copy rather than the message flow's: an article translation runs
		// for minutes, so the author is usually somewhere else by the time it fails,
		// and "Translation failed." would not tell them which of the two it was.
		subscribeRealtime({ type: EventType.ARTICLE_TRANSLATION_FAILED }, (message) => {
			announceFailure(message, ['reason'], 'Article translation failed.')
		}),
	]

	return () => {
		for (const unsubscribe of unsubscribes) unsubscribe()
	}
}
